import React from 'react';

import { UiButton } from '@nfctron/nexus-react';

import { Alert, Collapse, Text, Title } from '@mantine/core';
import { type ContextModalProps } from '@mantine/modals';
import Image from 'next/image';

import { type ApiError } from '@root/features/api-client/api-client';

import { APP_I18N_NAMESPACE } from '@root/lib/constants/common';
import { useDebug } from '@root/lib/hooks/useDebug';
import { useTranslate } from '@root/lib/hooks/useTranslation';
import { type ModalComponent } from '@root/lib/types/components';

/**
 * OrderCreateErrorModal modal
 * @author filipditrich <ditrich@nfctron.com>
 * @constructor
 */
export const OrderCreateErrorModal: ModalComponent<ContextModalProps<OrderCreateErrorModalProps>> = ({
	id,
	context,
	innerProps: { onButtonClick, error },
}) => {
	const { t } = useTranslate(APP_I18N_NAMESPACE.COMMON, OrderCreateErrorModal.displayName);
	const { isDebug } = useDebug();

	return (
		<React.Fragment>
			<div className="flex flex-col gap-6">
				{/* hero image */}
				<Image className="mx-auto" src="/static/images/blob.svg" alt={t('heading')} width={300} height={300} />
				{/* text */}
				<div className="text-center">
					<Text fz={20} c="lucky-point" fw={700} mb={3}>
						{t('heading')}
					</Text>
					<Text size="md">{t('text')}</Text>
				</div>
				{/* debug error */}
				<Collapse in={isDebug}>
					<Alert
						color="red"
						title={
							<Title size="smaller" fw={700}>
								{t('alert.title')}
							</Title>
						}
						radius="lg"
						classNames={{ body: 'max-w-full' }}
					>
						<div className="max-w-full overflow-scroll rounded-md bg-white/80 p-2">
							<pre className="text-md">{JSON.stringify(error, null, 4)}</pre>
						</div>
					</Alert>
				</Collapse>
				{/* close button */}
				<UiButton variant="filled" color="orange" onClick={async () => onButtonClick({ ...context, id })}>
					{t('button.text')}
				</UiButton>
			</div>
		</React.Fragment>
	);
};

/**
 * OrderCreateErrorModal modal props type
 * @export
 */
export type OrderCreateErrorModalProps = {
	error: ApiError;
	onButtonClick: (context: ContextModalProps['context'] & { id: string }) => void;
};

/**
 * OrderCreateErrorModal modal display name
 * @type {ComponentDisplayName}
 */
OrderCreateErrorModal.displayName = 'modal/OrderCreateErrorModal';
